import { type SessionUser } from "./types";

// The browser talks only to the BFF, same-origin. Auth lives at `/auth/*`
// (NOT under `/api`, which is the backend proxy). Cookies are httpOnly and
// sent automatically with `credentials: "include"`.

export interface DevLoginInput {
  email?: string;
  name?: string;
  role?: SessionUser["role"];
}

/** What the login screen needs to pick its CTA. */
export interface AuthConfig {
  /** The real Google OAuth flow is configured (client creds present). */
  googleEnabled: boolean;
  /** The dev-login stub is mounted (local fallback). */
  devLoginEnabled: boolean;
  /** Interim email+password login is available (DMS auth plan). */
  passwordEnabled?: boolean;
}

/** Why an email+password login failed (drives the screen's message). */
export type PasswordLoginReason = "invalid" | "domain" | "server";

/** Thrown by passwordLogin so the screen can show the right message. */
export class PasswordLoginError extends Error {
  constructor(readonly reason: PasswordLoginReason) {
    super(reason);
    this.name = "PasswordLoginError";
  }
}

/** Reads which sign-in methods the BFF has enabled. */
export const fetchAuthConfig = async (): Promise<AuthConfig> => {
  const response = await fetch("/auth/config", { credentials: "include" });
  if (!response.ok) {
    throw new Error(`Unexpected /auth/config status ${response.status}`);
  }
  return (await response.json()) as AuthConfig;
};

/** Full-page redirect target that starts the real Google OAuth flow. */
export const googleLoginHref = (from?: string): string =>
  from
    ? `/auth/google/login?from=${encodeURIComponent(from)}`
    : "/auth/google/login";

const json = async (response: Response): Promise<{ user: SessionUser }> =>
  (await response.json()) as { user: SessionUser };

/** Returns the current session, or null when not authenticated. */
export const fetchSession = async (): Promise<SessionUser | null> => {
  const response = await fetch("/auth/me", { credentials: "include" });
  if (response.status === 401) return null;
  if (!response.ok) throw new Error(`Unexpected /auth/me status ${response.status}`);
  return (await json(response)).user;
};

/** STUB — exchanges a dev identity for a session (stands in for Google OAuth). */
export const devLogin = async (input: DevLoginInput = {}): Promise<SessionUser> => {
  const response = await fetch("/auth/dev-login", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw new Error(`dev-login failed with ${response.status}`);
  return (await json(response)).user;
};

/** Interim email+password sign-in (DMS auth plan): posts to the BFF, which
 *  verifies against the backend and mints the httpOnly session. Throws a
 *  PasswordLoginError on 401 (invalid) / 403 (domain not allowed) / other
 *  (server). */
export const passwordLogin = async (
  email: string,
  password: string,
): Promise<SessionUser> => {
  const response = await fetch("/auth/login", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (response.ok) return (await json(response)).user;
  if (response.status === 401) throw new PasswordLoginError("invalid");
  if (response.status === 403) throw new PasswordLoginError("domain");
  throw new PasswordLoginError("server");
};

export const logout = async (): Promise<void> => {
  await fetch("/auth/logout", { method: "POST", credentials: "include" });
};
